import { KnowledgeBaseType } from '@constants/knowledge-base';
import { MB } from '@constants/file';
import { validateFileSize } from '@helpers/upload-file';

export type AgentContentCounts = {
    documentCount?: number;
    websiteCount?: number;
};

type ContentLimit = {
    perAgent: number;
    count: (agent: AgentContentCounts) => number;
};

const CONTENT_LIMITS: Record<number, ContentLimit> = {
    [KnowledgeBaseType.DOCUMENT]: { perAgent: 20, count: agent => agent.documentCount ?? 0 },
    [KnowledgeBaseType.WEBSITE]: { perAgent: 5, count: agent => agent.websiteCount ?? 0 },
};

export const DOCUMENT_EXTENSIONS = [
    '.pdf',
    '.doc',
    '.docx',
    '.ppt',
    '.pptx',
    '.xlsx',
    '.csv',
    '.txt',
    '.md',
    '.png',
    '.jpg',
    '.jpeg',
    '.webp',
];

export const DOCUMENT_LIMITS = { size: 10, metric: 'MB', pages: 100, rows: 1000 };

const WEBSITE_ADDRESS =
    /^(https?:\/\/)?([\p{L}\p{N}]([\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+[\p{L}\p{N}-]{2,}(\/[^\s*?#]*)?$/iu;

export function agentAtLimit(agent: AgentContentCounts, type: number): boolean {
    const limit = CONTENT_LIMITS[type];
    return !!limit && limit.count(agent) >= limit.perAgent;
}

export function everyAgentAtLimit(agents: AgentContentCounts[], type: number): boolean {
    return agents.length > 0 && agents.every(agent => agentAtLimit(agent, type));
}

export function agentsWithRoom<Id>(agentIds: Id[], agents: (AgentContentCounts & { id: Id })[], type: number): Id[] {
    return agentIds.filter(id => {
        const agent = agents.find(candidate => candidate.id === id);
        return !agent || !agentAtLimit(agent, type);
    });
}

export function documentFileError(file: File): { key: string; params?: Record<string, unknown> } | null {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!file.name.includes('.') || !DOCUMENT_EXTENSIONS.includes(extension)) {
        return { key: 'apiErrors.invalid_format' };
    }
    const { error, size, metric } = validateFileSize(file, DOCUMENT_LIMITS.size * MB);
    return error ? { key: `apiErrors.${error}`, params: { ...DOCUMENT_LIMITS, size, metric } } : null;
}

export function isWebsiteAddress(value: string): boolean {
    return WEBSITE_ADDRESS.test(value.trim());
}
